import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../../components/LanguageSelector";
import {
    User,
    Edit,
    MapPin,
    Globe,
    Star,
    Shield,
    Link as LinkIcon,
    Award,
    FileText,
    Clock,
    DollarSign,
    Briefcase,
    Calendar,
    Languages,
    Mail,
    Phone,
    MessageSquare,
    X,
    Settings,
    Check,
    Camera,
    Building,
    Home,
    Users,
    ChartAreaIcon,
    Menu,
    Bell,
    Trash2,
    Plus,
    LogOut,
} from "lucide-react";
import Button from "../../../components/ui/Button";
import Card from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { BiAnalyse } from "react-icons/bi";
import { useSelector, useDispatch } from "react-redux";
import ClientReviewsTab from "../../../components/ui/ClientReviewsTab";
import { getClientProgress } from "../../../redux/Actions/UserActions";
import { RootState, AppDispatch } from "../../../redux/store";
import { getAchievementConfig } from "../../../redux/Actions/AchievementActions";
import {
    createUserCompany,
    getAllCurrencies,
    getAllLanguages,
    getAllTimezones,
    getUserCompanies,
    getUserGeneralSetting,
    getUserPreferences,
    updateUserCompanies,
    UpdateUserProfile,
    uploadUserImage,
    useAppDispatch,
    getUserById,
    validateVATNumber,
    LogoutManually,
    LogoutUser,
    getClientStats,
} from "../../../redux";
import { hideLoader, selectLoaderValue, showLoader } from "../../../redux/Slices/loadingSlice";
import AboutComponent from "./ClientProfileComponents/AboutComponent";
import CompanyClientComponent from "./ClientProfileComponents/CompanyClientComponent";
import SharedHeader5 from "../../../Headers/SharedHeader5";
import { ValidationCompanyErrors, ValidationPersonalErrors } from "../../user";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { languagePreferences } from "../../shared";
import LanguageComponent from "./ClientProfileComponents/LanguageComponent";
import Spinner from "../../shared/components/Spinner";
import { GlobalService } from "../../shared/services";
import ContactComponent from "./ClientProfileComponents/ContactComponent";
import ImageSection from "./ClientProfileComponents/ImageSection";
import LocationComponent from "./ClientProfileComponents/LocationComponent";
import { RoleEnum } from "../../shared/model";
import SharedFooter3 from "../../../Footer/SharedFooter3";
import { ContractStatusEnum } from "../../shared/model/contract.enum";
import { MilestoneStatusEnum } from "../../shared/model";
import { getDynamicCurrencySymbol } from "../../../lib/currencyUtils";
// import CompanyComponent from "./ClientProfileComponents/CompanyComponent";
const ClientProfile = ({ isAdminEdit = false, adminUserId = null, canEdit = true, onUserUpdated = null }) => {
    const { t } = useTranslation();
    const { user, generalSetting, viewedUser } = useSelector((state: any) => state.auth);
    const { company, userPreferences, clientStats } = useSelector((state: any) => state.client);
    const { clientProgress, clientProgressLoading, clientProgressError } = useSelector((state: RootState) => state.user);
    const { clientConfig } = useSelector((state: RootState) => state.achievements);
    const dispatch = useDispatch<AppDispatch>();
    const { userId: urlUserId } = useParams(); // Get userId from URL params

    // If admin is editing, use adminUserId, otherwise use normal logic
    const isCurrentUser = isAdminEdit ? true : (!urlUserId || urlUserId === user?.id?.toString());
    const displayUserId = isAdminEdit ? adminUserId : (urlUserId || user?.id);

    // Determine which user data to display
    const displayUser = isAdminEdit ? viewedUser : (isCurrentUser ? user : viewedUser);

    // Determine if sensitive information should be masked
    const shouldMaskSensitiveInfo = !user || user.id !== displayUser?.id || !canEdit;

    // Helper function to mask sensitive information
    const maskSensitiveInfo = (value: string, type: "email" | "phone" | "vat" | "whatsapp" = "phone") => {
        if (!value || !shouldMaskSensitiveInfo) return value;
        
        switch (type) {
            case "email": {
                const [emailPrefix, emailDomain] = value.split("@");
                if (emailPrefix.length <= 2) return "***@" + emailDomain;
                return emailPrefix.substring(0, 2) + "***@" + emailDomain;
            }
            case "phone":
            case "whatsapp":
                if (value.length <= 4) return "***";
                return value.substring(0, 2) + "***" + value.substring(value.length - 2);
            case "vat":
                if (value.length <= 4) return "***";
                return value.substring(0, 2) + "***" + value.substring(value.length - 2);
            default:
                return "***";
        }
    };

    const formatJoinedDate = React.useCallback((dateString?: string | null) => {
        if (!dateString) return "January 2023";

        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return "January 2023";

        const options: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "long"
        };
        return date.toLocaleDateString("en-US", options);
    }, []);

    const isImageUrl = (url?: string | null) => {
        if (!url || typeof url !== "string") return false;

        const cleanUrl = url.split("#")[0].split("?")[0];
        const extension = cleanUrl.split(".").pop()?.toLowerCase();

        if (!extension) {
            return false;
        }

        const allowedExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "tif", "tiff", "heic", "heif"];
        return allowedExtensions.includes(extension);
    };

    // Helper function to check if user has any contracts with escrows in status 2 or 3
    // where the contract's professional ID matches the current logged-in user's ID
    const hasAcceptedMilestones = () => {
        if (!clientStats?.contracts || !Array.isArray(clientStats.contracts)) {
            return false;
        }

        const hasAccepted = clientStats.contracts.some((contract: any) => {
            // Check if contract has escrows array
            if (!contract.escrows || !Array.isArray(contract.escrows)) {
                return false;
            }

            // Check if contract's professional ID matches current user's ID
            if (!contract.professional?.id || contract.professional.id !== user?.id) {
                return false;
            }

            // Check if any escrow has status 2 or 3
            return contract.escrows.some((escrow: any) => {
                const statusId = Number(escrow.status?.id);
                return statusId === 2 || statusId === 3;
            });
        });

        // Debug logging for escrow check
        console.log("Escrow check:", {
            hasAccepted,
            currentUserId: user?.id,
            contractsCount: clientStats?.contracts?.length || 0,
            contracts: clientStats?.contracts?.map((c: any) => ({
                id: c.id,
                professionalId: c.professional?.id,
                escrows: c.escrows?.map((e: any) => ({
                    id: e.id,
                    status: e.status?.id,
                    statusName: e.status?.name
                })) || []
            })) || []
        });

        return hasAccepted;
    };

    // Debug logging for admin mode
    if (isAdminEdit) {
        console.log("Admin Edit Mode Debug:", {
            adminUserId,
            viewedUser: viewedUser?.id,
            user: user?.id,
            displayUser: displayUser?.id,
            isCurrentUser,
            shouldMaskSensitiveInfo
        });
    }
    const [isLanguageEditing, setIsLanguageEditing] = useState(false);
    const isLoading = useSelector(selectLoaderValue);
    const [isProfileLoading, setIsProfileLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedTab, setSelectedTab] = useState("overview");
    const [editedProfile, setEditedProfile] = useState(null);
    const [editingContact, setEditingContact] = useState(false);
    const [editingLocation, setEditingLocation] = useState(false);

    const navigate = useNavigate();
    const [validationPersonalErrors, setValidationPersonalErrors] = useState<ValidationPersonalErrors>({
        personal: {},
    });
    const [validationCompanyErrors, setValidationCompanyErrors] = useState<ValidationCompanyErrors>({
        company: {},
    });

    const [editingLanguages, setEditingLanguages] = useState([]);
    const [hasAddedNewLanguage, setHasAddedNewLanguage] = useState(false);
    const removedImagesRef = useRef(new Set()); // Track intentionally removed images
    
    // Use new languageLevels field - this is the primary source
    const preferredLanguages = React.useMemo(() => {
        if (!generalSetting) return [];
        
        console.log("=== generalSetting received ===", generalSetting);
        
        // Priority 1: Use languageLevels array (new structure with individual levels per language)
        if (generalSetting.languageLevels && generalSetting.languageLevels.length > 0) {
            console.log("✅ Using languageLevels:", generalSetting.languageLevels);
            const mapped = generalSetting.languageLevels.map((langLevel, index) => {
                const languageId = langLevel.language?.id || langLevel.language;
                const level = String(langLevel.level); // Ensure it's a string
                console.log(`Language ${index + 1}:`, { 
                    languageId, 
                    languageName: langLevel.language?.name,
                    level,
                    levelType: typeof langLevel.level,
                    convertedLevelType: typeof level
                });
                return {
                    id: generalSetting.id,
                    language: languageId,
                    languagePreferences: level, // Individual level per language ✅
                };
            });
            console.log("=== Mapped from languageLevels ===", mapped);
            return mapped;
        }
        
        // No fallback - only use languageLevels
        console.log("⚠️ No languageLevels found in generalSetting");
        
        console.log("❌ No language data found");
        return [];
    }, [generalSetting]);

    // Determine currency symbol from user's general settings using centralized utility
    const currencySymbol = React.useMemo(() => {
        return getDynamicCurrencySymbol();
    }, [generalSetting, displayUserId]);

    // Reset editing states when viewing different profile
    useEffect(() => {
        if (!isCurrentUser) {
            setIsEditing(false);
            setEditingContact(false);
            setEditingLocation(false);
            setIsLanguageEditing(false);
            setEditedProfile(null);
        }
    }, [isCurrentUser, urlUserId]);

    // Auto-open About component for editing if user has no phone number
    useEffect(() => {
        if (isCurrentUser && user && !user.phone && !isAdminEdit && canEdit) {
            // Small delay to ensure the component is fully loaded
            const timer = setTimeout(() => {
                startEditing();
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [isCurrentUser, user, isAdminEdit, canEdit]);

    // When you first load the profile data:
    useEffect(() => {
        if (isCurrentUser) {
            getProfileData();
        }
    }, [isCurrentUser]);

    // Fetch data for viewed users or admin editing
    useEffect(() => {
        if ((!isCurrentUser && urlUserId) || isAdminEdit) {
            getViewedUserProfileData();
        }
    }, [urlUserId, isCurrentUser, isAdminEdit, adminUserId]);

    // Reset editing states when switching between different users in admin mode
    useEffect(() => {
        if (isAdminEdit && adminUserId) {
            // Reset all editing states
            setIsEditing(false);
            setEditingContact(false);
            setEditingLocation(false);
            setIsLanguageEditing(false);
            setEditedProfile(null);
        }
    }, [adminUserId, isAdminEdit]);

    // Fetch client progress data
    useEffect(() => {
        if (displayUserId) {
            dispatch(getClientProgress({ userId: displayUserId }));
        }
    }, [dispatch, displayUserId]);

    // Fetch achievement configuration
    useEffect(() => {
        dispatch(getAchievementConfig("client"));
    }, [dispatch]);

    // Calculate client achievements based on real data and config
    const calculateClientAchievements = () => {
        if (!clientProgress) return [];

        const achievements = [];
        const currentYear = new Date().getFullYear();
        const memberYear = new Date(clientProgress.memberSince).getFullYear();
        
        // Calculate success rate from contracts
        const totalContracts = clientProgress.completedProjects + clientProgress.activeProjects;
        const successRate = totalContracts > 0 ? (clientProgress.completedProjects / totalContracts) * 100 : 0;

        // Get thresholds from config or use defaults
        const config = clientConfig?.thresholds || {
            topClientSpentAmount: 10000,
            topClientSuccessRate: 80,
            powerClientProjectsCount: 10,
            teamBuilderProjectsCount: 5,
            teamBuilderSuccessRate: 90,
            trustedReviewerCount: 10,
        };

        // Early Adopter - Joined in current year
        if (memberYear === currentYear) {
            achievements.push({
                title: t("clientProfile.earlyAdopter"),
                description: t("clientProfile.earlyAdopterDesc"),
                achieved: true,
                icon: "Star"
            });
        }

        // Top Client - High spending and completion rate
        if (clientProgress.totalSpent >= config.topClientSpentAmount && successRate >= config.topClientSuccessRate) {
            achievements.push({
                title: t("clientProfile.topClient"),
                description: t("clientProfile.topClientDesc", { 
                    amount: `${getDynamicCurrencySymbol()}${clientProgress.totalSpent.toLocaleString()}`,
                    rate: successRate.toFixed(1)
                }),
                achieved: true,
                icon: "Award"
            });
        }

        // Team Builder - High success rate with multiple projects
        if (clientProgress.completedProjects >= config.teamBuilderProjectsCount && successRate >= config.teamBuilderSuccessRate) {
            achievements.push({
                title: t("clientProfile.teamBuilder"),
                description: t("clientProfile.teamBuilderDesc", {
                    count: clientProgress.completedProjects,
                    rate: successRate.toFixed(1)
                }),
                achieved: true,
                icon: "Users"
            });
        }

        // Power Client - High number of completed projects
        if (clientProgress.completedProjects >= config.powerClientProjectsCount) {
            achievements.push({
                title: t("clientProfile.powerClient"),
                description: t("clientProfile.powerClientDesc", { count: clientProgress.completedProjects }),
                achieved: true,
                icon: "Briefcase"
            });
        }

        // Trusted Reviewer - High number of reviews
        if (clientProgress.totalReviews >= config.trustedReviewerCount) {
            achievements.push({
                title: t("clientProfile.trustedReviewer"),
                description: t("clientProfile.trustedReviewerDesc", {
                    count: clientProgress.totalReviews,
                    rating: clientProgress.averageRating
                }),
                achieved: true,
                icon: "Star"
            });
        }

        // Add pending achievements
        if (clientProgress.totalSpent < config.topClientSpentAmount) {
            achievements.push({
                title: t("clientProfile.topClient"),
                description: t("clientProfile.topClientPendingDesc", {
                    amount: `${getDynamicCurrencySymbol()}${(config.topClientSpentAmount - clientProgress.totalSpent).toLocaleString()}`
                }),
                achieved: false,
                icon: "Award"
            });
        }

        if (clientProgress.completedProjects < config.powerClientProjectsCount) {
            achievements.push({
                title: t("clientProfile.powerClient"),
                description: t("clientProfile.powerClientPendingDesc", {
                    count: config.powerClientProjectsCount - clientProgress.completedProjects
                }),
                achieved: false,
                icon: "Briefcase"
            });
        }

        if (clientProgress.totalReviews < config.trustedReviewerCount) {
            achievements.push({
                title: t("clientProfile.trustedReviewer"),
                description: t("clientProfile.trustedReviewerPendingDesc", {
                    count: config.trustedReviewerCount - clientProgress.totalReviews
                }),
                achieved: false,
                icon: "Star"
            });
        }

        return achievements;
    };

    const startEditingLanguage = () => {
        if (!canEdit) return; // Prevent editing if canEdit is false
        // Start with current languages or empty array if none exist
        setEditingLanguages(
            preferredLanguages.length > 0 ? [...preferredLanguages] : [{ language: "", languagePreferences: "" }]
        );
        setIsLanguageEditing(true);
    };

    const handleLogout = async () => {
        try {
            const response = await dispatch(LogoutUser({})).unwrap();
        } catch (error: any) {
            if (error === 401) {
                dispatch(LogoutManually());
                console.log("Token expired");
            } else {
                console.log("Logout failed:", error);
            }
        } finally {
            navigate("/");
        }
    };

    const sidebarItems = [
        { icon: <Home className="h-5 w-5" />, label: t("postJob.dashboard"), href: "/clients" },
        { icon: <User className="h-5 w-5" />, label: t("postJob.myProfile"), href: "/client/profile", active: true },
        { icon: <Settings className="h-5 w-5" />, label: t("postJob.profileSettings"), href: "/client/profilesettings" },
        { icon: <FileText className="h-5 w-5" />, label: t("postJob.myOrders"), href: "/client/myorders" },
        { icon: <Bell className="h-5 w-5" />, label: t("postJob.myOffers"), href: "/client/myoffers" },
        { icon: <MessageSquare className="h-5 w-5" />, label: t("postJob.messageOrVideoCall"), href: "/client/messageandvideocall" },
        { icon: <ChartAreaIcon className="h-5 w-5" />, label: t("postJob.progress"), href: "/client/progress" },
        { icon: <Shield className="h-5 w-5" />, label: t("postJob.supportTickets"), href: "/client/support" },
        { icon: <DollarSign className="h-5 w-5" />, label: t("postJob.wallet"), href: "/client/Wallet" },
        { icon: <BiAnalyse className="h-5 w-5" />, label: t("postJob.report"), href: "/client/reports" },
        {
            icon: <LogOut className="h-5 w-5" />,
            label: t("postJob.logOut"),
            href: "/logout"
        },
    ];
    const [profileData, setProfileData] = useState({
        personal: {
            name: user?.firstName + " " + user?.lastName,
            title: user?.title,
            photo: user?.photo?.path || "",
            location: user?.address || "",
            timezone: "", // Add timezone field
            cover:
                user?.cover?.path ||
                "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3",

            bio: user?.bio || "",
            email: user?.email || "",
            phone: user?.phone || "",
            contactOnWhatsapp: user?.contactOnWhatsapp || false,
            whatsappNumber: user?.whatsappNumber || "",
            joined: "September 2023",
            lastActive: "Currently Active",
            verified: true,
            projectsPosted: 15,
            hiredProfessionals: 12,
            successRate: 95,
            totalSpent: 125000,
            responseTime: "2 hours",
            socialLinks: user?.socialLinks || [],
            company: {
                name: "",
                industry: "",
                size: "",
                founded: "",
                website: "",
                description: "",
            },
        },
        stats: {
            totalInvested: "0+",
            avgProjectValue: "£15,000",
            completionRate: 0,
            repeatHireRate: 85,
            avgRating: 4.9,
            totalReviews: 45,
        },
        projectPreferences: {
            projectTypes: [],
            expertise: [],
            industries: [],
        },
        hiring: {
            activeJobs: 0,
            totalHires: 0,
            successfulProjects: 22,
            preferredSkills: ["React.js", "Node.js", "Python", "AWS", "UI/UX Design", "Mobile Development"],
        },
        achievements: [
            {
                title: "Top Client 2023",
                description: "Recognized for exceptional project management and professional relationships",
            },
            { title: "95% Success Rate", description: "Maintained high project success rate across all engagements" },
            { title: "Team Builder", description: "Successfully built and managed diverse remote teams" },
        ],
        reviews: {
            professional: [
                {
                    id: 1,
                    freelancer: {
                        name: "John Doe",
                        photo: "https://randomuser.me/api/portraits/men/1.jpg",
                        title: "Senior Full Stack Developer",
                    },
                    rating: 5,
                    date: "2024-01-15",
                    project: "E-commerce Platform Development",
                    comment:
                        "Emma is an exceptional client to work with. Clear communication and timely feedback made the project a great success.",
                },
            ],
        },
        activeProjects: [
            {
                id: "PRJ-2024-001",
                title: "E-commerce Platform Development",
                status: "in_progress",
                completion: 75,
                budget: 25000,
                timeline: "3 months",
                startDate: "2024-01-01",
                dueDate: "2024-03-31",
                team: [
                    {
                        name: "John Doe",
                        role: "Lead Developer",
                        photo: "https://randomuser.me/api/portraits/men/1.jpg",
                    },
                    {
                        name: "Sarah Wilson",
                        role: "UI/UX Designer",
                        photo: "https://randomuser.me/api/portraits/women/1.jpg",
                    },
                ],
                technologies: ["React", "Node.js", "MongoDB", "AWS"],
                nextMilestone: "Development Phase Completion",
            },
        ],
    });



    const startEditing = () => {
        if (!canEdit) return; // Prevent editing if canEdit is false
        setEditedProfile({ ...profileData.personal });
        setIsEditing(true);
        setIsLanguageEditing(true);
        setEditingLanguages(
            preferredLanguages.length > 0 ? [...preferredLanguages] : [{ language: "", languagePreferences: "" }]
        );
    };
    useEffect(() => {
        setProfileData(prev => ({
            ...prev,
            personal: {
                ...prev.personal,
                ...(displayUser && {
                    name: `${displayUser.firstName || ""} ${displayUser.lastName || ""}`,
                    title: displayUser.title || "",
                    // Only update photo if it's not intentionally cleared (empty string means intentionally removed)
                    photo: removedImagesRef.current.has("profile") ? "" : (displayUser?.photo?.path || ""),
                    email: maskSensitiveInfo(displayUser.email || "", "email"),
                    phone: maskSensitiveInfo(displayUser.phone || "", "phone"),
                    taxCode: displayUser?.taxCode || "",
                    contactOnWhatsapp: displayUser?.contactOnWhatsapp || false,
                    whatsappNumber: maskSensitiveInfo(displayUser?.whatsappNumber || "", "whatsapp"),
                    // Only update cover if it's not intentionally cleared (empty string means intentionally removed)
                    cover: removedImagesRef.current.has("cover") ? "" : (displayUser?.cover?.path || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3"),
                    bio: displayUser?.bio || prev.personal.bio,
                    socialLinks: displayUser?.socialLinks || [],
                    location: displayUser?.address || displayUser?.location || "",
                    address: displayUser?.address || displayUser?.location || "",
                    latitude: displayUser?.latitude,
                    longitude: displayUser?.longitude,
                    province: displayUser?.province,
                    country: displayUser?.country,
                    timezone: generalSetting?.timezoneId?.name || "", // Add timezone
                    joined: formatJoinedDate(displayUser.createdAt),
                }),
                // Show company data for both current user and viewed users
                ...(company && {
                    company: {
                        name: company.name || "",
                        industry: company.industry?.id || "",
                        size: company.companySize?.id || "",
                        founded: company.year ? company.year.toString() : "",
                        website: company.website || "",
                        description: company.description || "",
                        vatNumber: maskSensitiveInfo(company.vatNumber || "", "vat"),
                        legalStatus: company.legalStatus?.id || "",
                        isVatValid: company.isVatValid || false,
                    },
                }),
            },
            // Show user preferences for both current user and viewed users
            ...(userPreferences && {
                projectPreferences: {
                    ...prev.projectPreferences,
                    projectTypes: userPreferences.projectTypes || [],
                    expertise: userPreferences.expertise || [],
                },
            }),
        }));
    }, [displayUser, company, userPreferences, isCurrentUser, generalSetting, isAdminEdit, adminUserId, viewedUser, displayUserId, formatJoinedDate]);

    const imageSectionProfileData = React.useMemo(() => {
        if (isCurrentUser || isAdminEdit) {
            return profileData;
        }

        const safeUser = displayUser || {};
        const maskValue = (value: string, type: "email" | "phone" | "whatsapp" | "vat" = "phone") =>
            shouldMaskSensitiveInfo ? maskSensitiveInfo(value, type) : value;

        const resolvedCover =
            safeUser?.cover?.path ||
            "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3";

        return {
            ...profileData,
            personal: {
                ...profileData.personal,
                name: `${safeUser?.firstName || ""} ${safeUser?.lastName || ""}`.trim() || safeUser?.name || "",
                title: safeUser?.title || "",
                photo: safeUser?.photo?.path || "",
                cover: resolvedCover,
                bio: safeUser?.bio || profileData.personal.bio,
                email: maskValue(safeUser?.email || "", "email"),
                phone: maskValue(safeUser?.phone || "", "phone"),
                contactOnWhatsapp: !!safeUser?.contactOnWhatsapp,
                whatsappNumber: maskValue(safeUser?.whatsappNumber || "", "whatsapp"),
                socialLinks: Array.isArray(safeUser?.socialLinks) ? safeUser.socialLinks : [],
                location: safeUser?.address || safeUser?.location || "",
                address: safeUser?.address || safeUser?.location || "",
                latitude: safeUser?.latitude,
                longitude: safeUser?.longitude,
                province: safeUser?.province,
                country: safeUser?.country,
                timezone: generalSetting?.timezoneId?.name || safeUser?.timezone || "",
                joined: formatJoinedDate(safeUser?.createdAt),
                verified: Boolean(safeUser?.isVerified ?? safeUser?.verified ?? profileData.personal.verified),
                company: {
                    ...profileData.personal.company,
                    name: company?.name || "",
                    industry: company?.industry?.id || "",
                    size: company?.companySize?.id || "",
                    founded: company?.year ? company.year.toString() : "",
                    website: company?.website || "",
                    description: company?.description || "",
                    vatNumber: maskValue(company?.vatNumber || "", "vat"),
                    legalStatus: company?.legalStatus?.id || "",
                    isVatValid: company?.isVatValid || false,
                },
            },
            projectPreferences: {
                ...profileData.projectPreferences,
                projectTypes: userPreferences?.projectTypes || [],
                expertise: userPreferences?.expertise || [],
            },
        };
    }, [
        profileData,
        isCurrentUser,
        isAdminEdit,
        displayUser,
        company,
        generalSetting,
        userPreferences,
        shouldMaskSensitiveInfo,
        formatJoinedDate,
    ]);

    const getProfileData = async () => {
        // Only fetch data for current user
        if (!isCurrentUser || !user?.id) return;

        dispatch(showLoader());
        await dispatch(getUserCompanies({ id: user.id, isShowLoader: false }));
        await dispatch(getUserPreferences({ id: user.id, isShowLoader: false }));
        await dispatch(getAllLanguages({ isShowLoader: false }));
        await dispatch(getAllCurrencies({ isShowLoader: false }));
        await dispatch(getAllTimezones({ isShowLoader: false }));
        await dispatch(getUserGeneralSetting({ id: user.id }));
        await dispatch(getClientStats({ id: user.id, isShowLoader: false }));

        dispatch(hideLoader());
    };

    const getViewedUserProfileData = async () => {
        // Fetch data for viewed user or admin editing
        const userId = isAdminEdit ? adminUserId : urlUserId;
        if (!userId) return;

        setIsProfileLoading(true);
        try {
            await dispatch(getUserById({ id: userId, isShowLoader: false }));
            await dispatch(getUserCompanies({ id: userId, isShowLoader: false }));
            await dispatch(getUserPreferences({ id: userId, isShowLoader: false }));
            await dispatch(getAllLanguages({ isShowLoader: false }));
            await dispatch(getAllCurrencies({ isShowLoader: false }));
            await dispatch(getAllTimezones({ isShowLoader: false }));
            await dispatch(getUserGeneralSetting({ id: userId, isShowLoader: false }));
            await dispatch(getClientStats({ id: userId, isShowLoader: false }));
        } catch (error) {
            console.error("Error fetching viewed user data:", error);
        }
        setIsProfileLoading(false);
    };

    const startEditingContact = () => {
        if (!canEdit) return; // Prevent editing if canEdit is false
        if (!editedProfile) {
            setEditedProfile({ ...profileData.personal });
        }
        setEditingContact(true);
    };
    const startEditingLocation = () => {
        if (!canEdit) return; // Prevent editing if canEdit is false
        if (!editedProfile) {
            setEditedProfile({ ...profileData.personal });
        }
        setEditingLocation(true);
    };
    const handleContactSaveChanges = async () => {
        // Reset validation errors
        setValidationPersonalErrors({
            personal: {},
        });
        setValidationCompanyErrors({
            company: {},
        });

        let hasErrors = false;
        const newValidationPersonalErrors: ValidationPersonalErrors = {
            personal: {},
        };
        const newValidationCompanyErrors: ValidationCompanyErrors = {
            company: {},
        };

        if (!editedProfile) return;

        // Track changes
        let hasSocialLinksChanges = false;
        let hasWebsiteChanges = false;

        // Check if social links have changed
        const originalLinks = profileData.personal.socialLinks || [];
        const currentLinks = editedProfile.socialLinks || [];

        if (originalLinks.length !== currentLinks.length) {
            hasSocialLinksChanges = true;
        } else if (originalLinks.some((link, index) => link !== currentLinks[index])) {
            hasSocialLinksChanges = true;
        }

        // Check if website has changed
        const originalWebsite = profileData.personal.company?.website || "";
        const currentWebsite = editedProfile.company?.website || "";
        console.log(currentWebsite, "currentWebsite", originalWebsite);

        if (currentWebsite !== originalWebsite) {
            hasWebsiteChanges = true;
        }
        // Validate social links only if they exist in the form
        if (currentLinks.length > 0 || hasSocialLinksChanges) {
            if (currentLinks.length === 0 || currentLinks.every(link => !link)) {
                newValidationPersonalErrors.personal.socialLinks = t("clientProfile.validation.socialLinksRequired");
                hasErrors = true;
            }
        }

        // Validate website only if it's being modified or was previously empty and user is trying to add it
        if (hasWebsiteChanges || (!originalWebsite && currentWebsite)) {
            if (!currentWebsite) {
                newValidationCompanyErrors.company.website = t("clientProfile.validation.websiteRequired");
                hasErrors = true;
            }
        }

        if (hasErrors) {
            setValidationPersonalErrors(newValidationPersonalErrors);
            setValidationCompanyErrors(newValidationCompanyErrors);
            return;
        }

        try {
            // Update social links if changed
            if (hasSocialLinksChanges) {
                const payloadUser = {
                    ...user,
                    socialLinks: editedProfile.socialLinks.filter(link => link), // remove empty links
                };
                // Remove email from payload to avoid "emailAlreadyExists" error
                delete payloadUser.email;
                const targetUserId = isAdminEdit ? adminUserId : user.id;
                await dispatch(UpdateUserProfile({ id: targetUserId, data: payloadUser }));
            }

            // Handle company website changes only if there are changes
            if (hasWebsiteChanges && editedProfile.company) {
                const basePayload = {
                    name: "",
                    industry: { id: 1 },
                    companySize: { id: 1 },
                    year: 0,
                    website: editedProfile.company.website,
                    description: "",
                    userId: { id: user?.id },
                };

                const targetUserId = isAdminEdit ? adminUserId : user.id;
                if (!company) {
                    const createPayload = {
                        ...basePayload,
                        country: "",
                        legalStatus: { id: 1 },
                        postalCode: "",
                        state: "",
                        city: "",
                        address: "",
                        phone: "",
                        email: "",
                        taxId: "",
                        vatNumber: "",
                        registrationNumber: "",
                        trading: "",
                        userId: { id: targetUserId },
                    };
                    await dispatch(createUserCompany({ data: createPayload }));
                } else {
                    await dispatch(updateUserCompanies({ id: company.id, data: basePayload }));
                }
            }

            // Update local state
            setProfileData(prev => ({
                ...prev,
                personal: {
                    ...prev.personal,
                    socialLinks: editedProfile.socialLinks.filter(link => link), // remove empty links
                    company: {
                        ...prev.personal.company,
                        website: editedProfile.company?.website || prev.personal.company?.website || "",
                    },
                },
            }));

            setEditingContact(false);
        } catch (error) {
            console.error("Error saving contact changes:", error);
        }

        // Call admin callback if provided
        if (isAdminEdit && onUserUpdated) {
            onUserUpdated();
        }
    };

    const handleLocationSaveChanges = async () => {
        // Reset validation errors
        setValidationPersonalErrors({
            personal: {},
        });

        let hasErrors = false;

        const newValidationPersonalErrors: ValidationPersonalErrors = {
            personal: {},
        };
        if (!editedProfile) return;

        // Track changes
        let hasLocationChanges = false;

        // Personal info validation - check for either location or address
        if (!editedProfile?.location && !editedProfile?.address) {
            newValidationPersonalErrors.personal.location = t("clientProfile.validation.locationRequired");
            hasErrors = true;
        } else if (
            editedProfile.location !== profileData.personal.location ||
            editedProfile.address !== (profileData.personal as any).address ||
            editedProfile.latitude !== (profileData.personal as any).latitude ||
            editedProfile.longitude !== (profileData.personal as any).longitude
        ) {
            hasLocationChanges = true;
        }
        if (hasErrors) {
            setValidationPersonalErrors(newValidationPersonalErrors);
            return;
        }

        try {
            // Handle location changes only if there are changes
            if (hasLocationChanges) {
                const payloadUser: any = {
                    ...user,
                    address: editedProfile.address || editedProfile.location,
                    latitude: editedProfile.latitude,
                    longitude: editedProfile.longitude,
                    location: editedProfile.location || editedProfile.address,
                };
                // Include province and country in user payload if available
                if (editedProfile.province) {
                    payloadUser.province = editedProfile.province;
                }
                if (editedProfile.country && editedProfile.country.id) {
                    payloadUser.country = { id: editedProfile.country.id };
                }
                const targetUserId = isAdminEdit ? adminUserId : user.id;
                const updateResult = await dispatch(UpdateUserProfile({ id: targetUserId, data: payloadUser }));
                
                // If update was successful and we have the updated user data, use it
                if (updateResult.payload && typeof updateResult.payload === "object") {
                    // The Redux state should already be updated by the action
                    // But let's also update our local state to ensure consistency
                    setProfileData(prev => ({
                        ...prev,
                        personal: {
                            ...prev.personal,
                            location: updateResult.payload.address || updateResult.payload.location || editedProfile?.address || editedProfile?.location,
                            address: updateResult.payload.address || editedProfile?.address || editedProfile?.location,
                            latitude: updateResult.payload.latitude || editedProfile?.latitude,
                            longitude: updateResult.payload.longitude || editedProfile?.longitude,
                            province: updateResult.payload.province || editedProfile?.province,
                            country: updateResult.payload.country || editedProfile?.country,
                        },
                    }));
                } else {
                    // Fallback: update local state with edited profile data
                    setProfileData(prev => ({
                        ...prev,
                        personal: {
                            ...prev.personal,
                            location: editedProfile?.address || editedProfile?.location,
                            address: editedProfile?.address || editedProfile?.location,
                            latitude: editedProfile?.latitude,
                            longitude: editedProfile?.longitude,
                            province: editedProfile?.province,
                            country: editedProfile?.country,
                        },
                    }));
                }
            }

            setEditingLocation(false);
        } catch (error) {
            console.error("Error saving location changes:", error);
        }

        // Call admin callback if provided
        if (isAdminEdit && onUserUpdated) {
            onUserUpdated();
        }
    };
    const handleInfoSaveChanges = async () => {
        // Reset validation errors
        setValidationPersonalErrors({
            personal: {},
        });

        let hasErrors = false;
        const newValidationErrors: ValidationPersonalErrors = {
            personal: {},
        };
        if (!editedProfile) return;
        // Track which sections have changes
        let hasPersonalChanges = false;
        let hasWhatsAppChanges = false;

        // Personal info validation
        if (!editedProfile?.name) {
            newValidationErrors.personal.name = t("clientProfile.validation.nameRequired");
            hasErrors = true;
        } else {
            // Check if name contains both first and last name
            const nameParts = editedProfile.name.trim().split(" ");
            if (nameParts.length < 2 || nameParts[1] === "") {
                newValidationErrors.personal.name = t("clientProfile.validation.enterFullName");
                hasErrors = true;
            } else if (editedProfile.name !== profileData.personal.name) {
                hasPersonalChanges = true;
            }
        }

        if (!editedProfile?.bio) {
            newValidationErrors.personal.bio = t("clientProfile.validation.bioRequired");
            hasErrors = true;
        } else if (editedProfile.bio !== profileData.personal.bio) {
            hasPersonalChanges = true;
        }

        if (!editedProfile?.phone) {
            newValidationErrors.personal.phone = t("clientProfile.validation.phoneRequired");
            hasErrors = true;
        } else if (editedProfile.phone !== profileData.personal.phone) {
            hasPersonalChanges = true;
        }
        if (!editedProfile?.title) {
            newValidationErrors.personal.title = t("clientProfile.validation.titleRequired");
            hasErrors = true;
        } else if (editedProfile.title !== profileData.personal.title) {
            hasPersonalChanges = true;
        }

        // Check for WhatsApp changes
        if (editedProfile.contactOnWhatsapp !== profileData.personal.contactOnWhatsapp) {
            hasWhatsAppChanges = true;
        }
        if (editedProfile.whatsappNumber !== profileData.personal.whatsappNumber) {
            hasWhatsAppChanges = true;
        }

        // Company info validation

        if (hasErrors) {
            setValidationPersonalErrors(newValidationErrors);
            return;
        }
        // Only proceed if no validation errors
        setProfileData(prev => ({ ...prev, personal: { ...editedProfile } }));
        setIsEditing(false);

        try {
            // Update user if personal info or WhatsApp info changed
            if (hasPersonalChanges || hasWhatsAppChanges) {
                const [firstName, ...rest] = (editedProfile.name || "").split(" ");
                const lastName = rest.join(" ");

                const payloadUser = {
                    firstName,
                    lastName,
                    title: editedProfile.title,
                    bio: editedProfile.bio,
                    phone: editedProfile.phone,
                    contactOnWhatsapp: editedProfile.contactOnWhatsapp || false,
                    whatsappNumber: editedProfile.whatsappNumber || null,
                    socialLinks: editedProfile?.socialLinks || [],
                    // Include photo object if it exists
                    ...(user?.photo && { photo: user.photo }),
                    // Include cover object if it exists
                    ...(user?.cover && { cover: user.cover }),
                };
                // Don't include role or status - keep the target user's existing role/status

                const targetUserId = isAdminEdit ? adminUserId : user.id;
                await dispatch(UpdateUserProfile({ id: targetUserId, data: payloadUser }));
            }
        } catch (error) {
            console.error("Error saving changes:", error);
        }

        // Call admin callback if provided
        if (isAdminEdit && onUserUpdated) {
            onUserUpdated();
        }
    };
    const handleCompanySaveChanges = async () => {
        // Reset validation errors
        setValidationCompanyErrors({
            company: {},
        });

        let hasErrors = false;
        const newValidationErrors: ValidationCompanyErrors = {
            company: {},
        };
        if (!editedProfile) return;
        // Track which sections have changes
        let hasCompanyChanges = false;

        // Company info validation
        if (editedProfile.company) {
            const companyWasEmpty =
                !profileData.personal.company ||
                (!profileData.personal.company.name &&
                    !profileData.personal.company.description &&
                    !profileData.personal.company.founded &&
                    !profileData.personal.company.website &&
                    !profileData.personal.company.industry &&
                    !profileData.personal.company.size);

            // Only require validation if:
            // 1. Company wasn't empty originally OR
            // 2. User has modified at least one company field
            const isVatOnlyUpdate = (
                (editedProfile.company.vatNumber !== (profileData.personal.company as any)?.vatNumber)
                && !editedProfile.company.name
                && !editedProfile.company.description
                && !editedProfile.company.founded
                && !editedProfile.company.website
                && !editedProfile.company.industry
                && !editedProfile.company.size
            );

            const shouldValidateCompany =
                (!companyWasEmpty || (
                    editedProfile.company.name ||
                    editedProfile.company.description ||
                    editedProfile.company.founded ||
                    editedProfile.company.website ||
                    editedProfile.company.industry ||
                    editedProfile.company.size
                )) && !isVatOnlyUpdate;

            if (shouldValidateCompany) {
                if (!editedProfile.company.name) {
                    newValidationErrors.company.name = t("clientProfile.validation.companyNameRequired");
                    hasErrors = true;
                } else if (editedProfile.company.name !== profileData.personal.company.name) {
                    hasCompanyChanges = true;
                }

                if (!editedProfile.company.description) {
                    newValidationErrors.company.description = t("clientProfile.validation.companyDescriptionRequired");
                    hasErrors = true;
                } else if (editedProfile.company.description !== profileData.personal.company.description) {
                    hasCompanyChanges = true;
                }
                if (!editedProfile.company.founded) {
                    newValidationErrors.company.founded = t("clientProfile.validation.foundedYearRequired");
                    hasErrors = true;
                } else if (!/^\d{4}$/.test(editedProfile.company.founded)) {
                    newValidationErrors.company.founded = t("clientProfile.validation.mustBeValidYear");
                    hasErrors = true;
                } else if (parseInt(editedProfile.company.founded) > new Date().getFullYear()) {
                    newValidationErrors.company.founded = t("clientProfile.validation.yearCannotBeFuture");
                    hasErrors = true;
                } else if (editedProfile.company.founded !== profileData.personal.company.founded) {
                    hasCompanyChanges = true;
                }
                // Website optional; only mark change when different
                if (editedProfile.company.website !== profileData.personal.company.website) {
                    hasCompanyChanges = true;
                }
                if (!editedProfile.company.industry) {
                    newValidationErrors.company.industry = t("clientProfile.validation.industryRequired");
                    hasErrors = true;
                } else if (editedProfile.company.industry !== profileData.personal.company.industry) {
                    hasCompanyChanges = true;
                }
                if (!editedProfile.company.size) {
                    newValidationErrors.company.companySize = t("clientProfile.validation.companySizeRequired");
                    hasErrors = true;
                } else if (editedProfile.company.size !== profileData.personal.company.size) {
                    hasCompanyChanges = true;
                }

                // Check for VAT number changes
                if (editedProfile.company.vatNumber !== (profileData.personal.company as any)?.vatNumber) {
                    hasCompanyChanges = true;
                }

                // Check for legal status changes
                if (editedProfile.company.legalStatus !== (profileData.personal.company as any)?.legalStatus) {
                    hasCompanyChanges = true;
                }
            } else {
                // If VAT-only update, still mark changes when value changed
                if (editedProfile.company.vatNumber !== (profileData.personal.company as any)?.vatNumber) {
                    hasCompanyChanges = true;
                }
            }
        }
        if (hasErrors) {
            setValidationCompanyErrors(newValidationErrors);
            return;
        }

        // Require vatNumber for company save
        if (!editedProfile.company?.vatNumber || editedProfile.company.vatNumber.trim() === "") {
            (newValidationErrors.company as any).vatNumber = t("clientProfile.validation.vatNumberRequired");
            setValidationCompanyErrors(newValidationErrors);
            return;
        }

        // ID validation logic (only verify via VIES when selected)
        let isVatValid = false;
        const selectedIdType = editedProfile.company?.idType;
        const shouldVerifyVies = selectedIdType === "vatNumberVies";
        if (shouldVerifyVies && editedProfile.company?.vatNumber) {
            // Extract country code from the VAT number
            const extractedCountryCode = extractCountryCodeFromVAT(editedProfile.company.vatNumber);

            if (!extractedCountryCode) {
                GlobalService.showToast("error", t("clientProfile.vatValidation"), t("clientProfile.validation.invalidVatFormat"));
                return;
            }

            // Extract VAT number without country code
            const vatNumberWithoutCountryCode = editedProfile.company.vatNumber.substring(2);

            dispatch(showLoader());
            try {
                // Validate VAT number using VIES API
                const vatValidationResponse = await dispatch(validateVATNumber({
                    countryCode: extractedCountryCode,
                    vatNumber: vatNumberWithoutCountryCode,
                    companyId: company?.id
                })).unwrap();
                isVatValid = vatValidationResponse.valid;

                if (vatValidationResponse.valid) {
                    GlobalService.showToast("success", t("clientProfile.vatValidation"), t("clientProfile.validation.vatValid"));
                } else {
                    GlobalService.showToast("error", t("clientProfile.vatValidation"), t("clientProfile.validation.vatInvalid"));
                    // return;
                }
            } catch (error) {
                console.error("VAT validation error:", error);
                GlobalService.showToast("error", t("clientProfile.vatValidation"), t("clientProfile.validation.vatValidationFailed"));
                return;
            }
        } else if (company) {
            // Preserve existing VAT validation status if present
            isVatValid = company.isVatValid || false;
        }

        // Only proceed if no validation errors
        setProfileData(prev => ({ ...prev, personal: { ...editedProfile } }));
        setIsEditing(false);
        setEditingContact(false);

        try {
            // Only update company if company info changed
            if (hasCompanyChanges && editedProfile.company) {
                const targetUserId = isAdminEdit ? adminUserId : user?.id;
                const basePayload = {
                    name: editedProfile.company.name,
                    industry: { id: Number(editedProfile.company.industry) },
                    companySize: { id: Number(editedProfile.company.size) },
                    year: parseInt(editedProfile.company.founded, 10),
                    website: editedProfile.company.website,
                    description: editedProfile.company.description,
                    vatNumber: editedProfile.company.vatNumber || "",
                    legalStatus: { id: Number(editedProfile.company.legalStatus) || 1 },
                    isVatValid,
                    userId: { id: targetUserId },
                };

                if (company == null) {
                    const createPayload = {
                        ...basePayload,
                        country: "",
                        postalCode: "",
                        state: "",
                        city: "",
                        address: "",
                        phone: "",
                        email: "",
                        taxId: "",
                        registrationNumber: "",
                        trading: "",
                    };
                    await dispatch(createUserCompany({ data: createPayload }));
                } else {
                    await dispatch(updateUserCompanies({ id: company?.id, data: basePayload }));
                }
            }
        } catch (error) {
            console.error("Error saving changes:", error);
        }

        // Call admin callback if provided
        if (isAdminEdit && onUserUpdated) {
            onUserUpdated();
        }
    };

    const handleCancelEdit = () => {
        setEditedProfile(null);
        setIsEditing(false);
        setEditingContact(false);
        setEditingLocation(false);
    };

    const handleFieldChange = (field, value) => {
        setEditedProfile(prev => ({ ...prev, [field]: value }));
    };

    const handleCompanyFieldChange = (companyField, value) => {
        setEditedProfile(prev => ({
            ...prev,
            company: { ...prev.company, [companyField]: value },
        }));
    };

    // Function to extract country code from VAT number
    const extractCountryCodeFromVAT = (vatNumber: string): string => {
        if (!vatNumber || vatNumber.length < 2) return "";

        // VAT numbers typically start with 2 letters (country code)
        const countryCode = vatNumber.substring(0, 2).toUpperCase();

        // Validate that it's actually letters
        if (/^[A-Z]{2}$/.test(countryCode)) {
            return countryCode;
        }

        return "";
    };
    const handleSocialLinkChange = (index: number, value: string) => {
        setEditedProfile(prev => {
            if (!prev) return prev; // Guard clause if prev is null/undefined

            // Create a new array with the updated link
            const updatedSocialLinks = prev.socialLinks
                ? [...prev.socialLinks] // Clone the existing array
                : []; // Fallback to empty array if socialLinks doesn't exist

            // Update the specific index
            updatedSocialLinks[index] = value;

            // Return a new object with the updated socialLinks
            return {
                ...prev,
                socialLinks: updatedSocialLinks,
            };
        });
    };

    const headerStyle =
        ".profile-photo-container{position:relative;margin:-3rem auto 1rem;z-index:10;width:fit-content}.edit-profile-image{position:absolute;bottom:0;right:0;background-color:rgba(0,0,0,0.6);color:white;border-radius:50%;padding:0.375rem;transition:all 0.2s}.edit-profile-image:hover{background-color:rgba(0,0,0,0.8)}";
    const handleProfilePhotoUpload = async e => {
        if (!canEdit) return; // Prevent editing if canEdit is false
        const file = e.target.files?.[0];
        if (!file) return;

        const { payload } = await dispatch(uploadUserImage({ file }));

        // Reset the removed flag for profile photo
        removedImagesRef.current.delete("profile");

        setProfileData(prev => ({
            ...prev,
            personal: {
                ...prev.personal,
                photo: payload?.file?.path,
            },
        }));

        const targetUserId = isAdminEdit ? adminUserId : user.id;
        const updateImageDataInBackground = {
            ...user,
            photo: {
                id: payload?.file?.id,
                path: payload?.file?.path,
            },
        };
        // Remove email from payload to avoid "emailAlreadyExists" error
        delete updateImageDataInBackground.email;

        await dispatch(UpdateUserProfile({ id: targetUserId, data: updateImageDataInBackground }));

        // Call admin callback if provided
        if (isAdminEdit && onUserUpdated) {
            onUserUpdated();
        }
    };

    const handleCoverPhotoUpload = async e => {
        if (!canEdit) return; // Prevent editing if canEdit is false
        const file = e.target.files?.[0];
        if (!file) return;

        const { payload } = await dispatch(uploadUserImage({ file }));

        // Reset the removed flag for cover photo
        removedImagesRef.current.delete("cover");

        setProfileData(prev => ({
            ...prev,
            personal: {
                ...prev.personal,
                cover: payload?.file?.path,
            },
        }));

        const targetUserId = isAdminEdit ? adminUserId : user.id;
        const updateImageDataInBackground = {
            ...user,
            cover: {
                id: payload?.file?.id,
                path: payload?.file?.path,
            },
        };
        // Remove email from payload to avoid "emailAlreadyExists" error
        delete updateImageDataInBackground.email;

        await dispatch(UpdateUserProfile({ id: targetUserId, data: updateImageDataInBackground }));

        // Call admin callback if provided
        if (isAdminEdit && onUserUpdated) {
            onUserUpdated();
        }
    };

    // Helper function to get status badge color (same as client dashboard)
    const getStatusColor = (statusId: number) => {
        switch (statusId) {
            case ContractStatusEnum.Completed:
                return "bg-green-100 text-green-800";
            case ContractStatusEnum.Active:
            case ContractStatusEnum.Accepted:
                return "bg-blue-100 text-blue-800";
            case ContractStatusEnum.Pending:
                return "bg-yellow-100 text-yellow-800";
            case ContractStatusEnum.Cancelled:
            case ContractStatusEnum.Rejected:
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    // Helper function to format date
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric"
        });
    };

    // Helper function to calculate delivery time from milestone dueDate
    const calculateDeliveryTime = (milestones: any[]) => {
        if (!milestones || milestones.length === 0 || !milestones[0]?.dueDate) {
            return "N/A";
        }

        const dueDate = new Date(milestones[0].dueDate);
        const now = new Date();
        const diffTime = dueDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return "Overdue";
        } else if (diffDays === 0) {
            return "Today";
        } else if (diffDays === 1) {
            return "1 day";
        } else if (diffDays < 7) {
            return `${diffDays} days`;
        } else if (diffDays < 30) {
            const weeks = Math.floor(diffDays / 7);
            return weeks === 1 ? "1 week" : `${weeks} weeks`;
        } else if (diffDays < 365) {
            const months = Math.floor(diffDays / 30);
            return months === 1 ? "1 month" : `${months} months`;
        } else {
            const years = Math.floor(diffDays / 365);
            return years === 1 ? "1 year" : `${years} years`;
        }
    };

    const handleRemoveProfileImage = async (type = "profile") => {
        if (!canEdit) return; // Prevent editing if canEdit is false
        if (!user?.id) return;

        console.log(`Starting to remove ${type} image, current user:`, user);
        console.log(`Current profileData.${type}:`, type === "profile" ? profileData.personal.photo : profileData.personal.cover);

        try {
            // Mark this image as intentionally removed
            removedImagesRef.current.add(type);

            const updateImageDataInBackground = {
                ...user,
                ...(type === "profile" ? { photo: null } : { cover: null }), // Remove photo or cover based on type
            };
            // Remove email from payload to avoid "emailAlreadyExists" error
            delete updateImageDataInBackground.email;

            console.log("Sending update data:", updateImageDataInBackground);

            const targetUserId = isAdminEdit ? adminUserId : user.id;
            const updatedUser = await dispatch(UpdateUserProfile({ id: targetUserId, data: updateImageDataInBackground }));

            console.log("Update response:", updatedUser);

            // Update local state
            setProfileData(prev => {
                const newState = {
                    ...prev,
                    personal: {
                        ...prev.personal,
                        ...(type === "profile" ? { photo: "" } : { cover: "" }), // Clear the photo or cover path
                    },
                };
                console.log("New profile data state:", newState);
                return newState;
            });

            // Update user state in Redux and local storage
            if (updatedUser.payload && typeof updatedUser.payload === "object") {
                // Update the user object in Redux state
                const newUser = { ...user, ...updatedUser.payload };

                console.log("New user object:", newUser);

                // Update local storage
                localStorage.setItem("user", JSON.stringify(newUser));

                // Note: The Redux state will be updated automatically by the UpdateUserProfile action
                // but we can also manually update it if needed
            }
        } catch (error) {
            console.error(`Error removing ${type} image:`, error);
            // Remove from ref if the operation failed
            removedImagesRef.current.delete(type);
        }

        // Call admin callback if provided
        if (isAdminEdit && onUserUpdated) {
            onUserUpdated();
        }
    };
    return (
        <>
            {isLoading || isProfileLoading ? (
                <div className="fixed left-[50%] top-[40%] z-[1000px]">
                    <Spinner color="green" />
                </div>
            ) : null}

            <div
                className={`min-h-screen bg-gray-50 w-full overflow-x-hidden ${isLoading || isProfileLoading ? "opacity-25" : "opacity-100"
                    }`}>
                <style>{headerStyle}</style>
                {/* Fixed Header */}
                {!isAdminEdit && (
                    <div className="fixed top-0 left-0 right-0 z-50 bg-white">
                        <SharedHeader5 />
                    </div>
                )}
                {/* Main Content with proper top padding to account for fixed header */}
                <div className={`flex min-h-screen ${!isAdminEdit ? "pt-14 sm:pt-16" : ""}`} style={{ textAlign: "left" }}>
                    {/* Desktop sidebar - only show for current user's own profile, not in admin edit mode, and when canEdit is true */}
                    {isCurrentUser && !isAdminEdit && canEdit && (
                        <aside className="hidden lg:block fixed left-0 top-14 sm:top-16 bottom-0 w-64 bg-white border-r border-gray-200 shadow-sm overflow-y-auto z-40">
                            <nav className="p-4 space-y-1">
                                {sidebarItems.map((item, index) => (
                                    item.href === "/logout" ? (
                                        <button
                                            key={index}
                                            onClick={handleLogout}
                                            className="flex w-full items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-gray-600 hover:bg-gray-50">
                                            {item.icon}
                                            <span>{item.label}</span>
                                        </button>
                                    ) :
                                        <NavLink
                                            key={index}
                                            to={item.href}
                                            className={({ isActive }) =>
                                                `flex items-center gap-3 px-4 py-2 rounded-lg text-sm transition-colors ${isActive || item.active
                                                    ? "text-blue-600 bg-blue-50 font-medium"
                                                    : "text-gray-600 hover:bg-gray-50"
                                                }`
                                            }>
                                            {item.icon}
                                            <span>{item.label}</span>
                                        </NavLink>
                                ))}
                            </nav>
                        </aside>
                    )}
                    <main className={`flex-1 w-full profile-content ${isCurrentUser && !isAdminEdit && canEdit ? "lg:pl-64" : ""}`}>
                        {!displayUser && !isCurrentUser && !isAdminEdit ? (
                            <div className="flex items-center justify-center h-64">
                                <div className="text-center">
                                    <p className="text-gray-500 text-lg">{t("clientProfile.loadingProfile")}</p>
                                </div>
                            </div>
                        ) : isAdminEdit && !viewedUser ? (
                            <div className="flex items-center justify-center h-64">
                                <div className="text-center">
                                    <p className="text-gray-500 text-lg">{t("clientProfile.loadingUserProfile")}</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <ImageSection
                                    isEditing={isEditing}
                                    handleCoverPhotoUpload={handleCoverPhotoUpload}
                                    profileData={imageSectionProfileData}
                                    editingContact={editingContact}
                                    startEditing={startEditing}
                                    handleProfilePhotoUpload={handleProfilePhotoUpload}
                                    isCurrentUser={isCurrentUser}
                                    userId={isAdminEdit ? adminUserId : displayUserId}
                                    onRemoveProfileImage={handleRemoveProfileImage}
                                    canEdit={canEdit}
                                />

                                <div className="max-w-6xl mx-auto py-6 px-4 pt-14 sm:px-6 w-full">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                                        <Card color={undefined} className="p-4 bg-blue-50 w-full">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="h-5 w-5 text-blue-600 inline-flex items-center justify-center font-semibold">{currencySymbol}</span>
                                                <Badge variant="secondary" className="bg-blue-100 text-blue-700 text-xs">
                                                    {t("clientProfile.totalInvested")}
                                                </Badge>
                                            </div>
                                            <p className="text-xl font-bold text-gray-900">
                                                {clientStats?.totalInvested ? `${currencySymbol}${clientStats.totalInvested.toLocaleString()}` : "0"}
                                            </p>
                                            <p className="text-xs text-gray-600">
                                                {t("clientProfile.acrossProjects", { count: clientStats?.jobsCount || 0 })}
                                            </p>
                                        </Card>
                                        <Card color={undefined} className="p-4 bg-green-50 w-full">
                                            <div className="flex items-center justify-between mb-2">
                                                <Check className="h-5 w-5 text-green-600" />
                                                <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                                                    {t("clientProfile.completedProjects")}
                                                </Badge>
                                            </div>
                                            <p className="text-xl font-bold text-gray-900">
                                                {clientStats?.completedContractsCount || 0}
                                            </p>
                                            <p className="text-xs text-gray-600">{t("clientProfile.successfullyCompleted")}</p>
                                        </Card>
                                        <Card color={undefined} className="p-4 bg-purple-50 w-full">
                                            <div className="flex items-center justify-between mb-2">
                                                <Briefcase className="h-5 w-5 text-purple-600" />
                                                <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">
                                                    {t("clientProfile.activeProjects")}
                                                </Badge>
                                            </div>
                                            <p className="text-xl font-bold text-gray-900">{clientStats?.activeContractsCount || 0}</p>
                                            <p className="text-xs text-gray-600">{t("clientProfile.currentlyInProgress")}</p>
                                        </Card>
                                        <Card color={undefined} className="p-4 bg-yellow-50 w-full">
                                            <div className="flex items-center justify-between mb-2">
                                                <Users className="h-5 w-5 text-yellow-600" />
                                                <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 text-xs">
                                                    {t("clientProfile.totalJobs")}
                                                </Badge>
                                            </div>
                                            <p className="text-xl font-bold text-gray-900">{clientStats?.jobsCount || 0}</p>
                                            <p className="text-xs text-gray-600">{t("clientProfile.jobsPosted")}</p>
                                        </Card>
                                    </div>
                                    <div className="mb-8 border-b overflow-x-auto">
                                        <div className="flex gap-4 px-2 min-w-max">
                                            {["overview", "projects", "reviews", "hiring"].map(tab => (
                                                <button
                                                    key={tab}
                                                    onClick={() => setSelectedTab(tab)}
                                                    className={`py-3 px-2 text-sm relative ${selectedTab === tab
                                                        ? "text-blue-600"
                                                        : "text-gray-600 hover:text-gray-900"
                                                        }`}>
                                                    <span>{t(`clientProfile.${tab}`)}</span>
                                                    {selectedTab === tab && (
                                                        <motion.div
                                                            layoutId="activeTab"
                                                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                                                        />
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex flex-col lg:flex-row gap-6 w-full">
                                        <div className="lg:w-2/3 space-y-6 w-full">
                                            {selectedTab === "overview" && (
                                                <>
                                                    <AboutComponent
                                                        isEditing={isEditing}
                                                        startEditing={startEditing}
                                                        handleFieldChange={handleFieldChange}
                                                        editedProfile={editedProfile}
                                                        profileData={profileData}
                                                        validationErrors={validationPersonalErrors.personal}
                                                        handleCancelEdit={handleCancelEdit}
                                                        handleSaveChanges={handleInfoSaveChanges}
                                                        isCurrentUser={isCurrentUser}
                                                        canEdit={canEdit}
                                                        shouldMaskSensitiveInfo={shouldMaskSensitiveInfo}
                                                    />

                                                    <CompanyClientComponent
                                                        isEditing={isEditing}
                                                        startEditing={startEditing}
                                                        handleCompanyFieldChange={handleCompanyFieldChange}
                                                        editedProfile={editedProfile}
                                                        profileData={profileData}
                                                        validationErrors={validationCompanyErrors.company}
                                                        handleCancelEdit={handleCancelEdit}
                                                        handleSaveChanges={handleCompanySaveChanges}
                                                        showEditIcon={canEdit}
                                                        setEditingCompany={() => { }}
                                                        isCurrentUser={isCurrentUser}
                                                        canEdit={canEdit}
                                                        shouldMaskSensitiveInfo={shouldMaskSensitiveInfo}
                                                    />
                                                    {(selectedTab === "overview" || window.innerWidth >= 1024) && (
                                                        <LocationComponent
                                                            editingLocation={editingLocation}
                                                            isEditing={isEditing}
                                                            startEditingLocation={startEditingLocation}
                                                            editedProfile={editedProfile}
                                                            handleFieldChange={handleFieldChange}
                                                            validationPersonalErrors={validationPersonalErrors}
                                                            profileData={profileData}
                                                            handleLocationSaveChanges={handleLocationSaveChanges}
                                                            setEditingLocation={setEditingLocation}
                                                            isCurrentUser={isCurrentUser}
                                                            showEditIcon={canEdit}
                                                            canEdit={canEdit}
                                                        />
                                                    )}
                                                    <Card color={undefined} className="p-4 w-full">
                                                        <div className="flex justify-between items-center">
                                                            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                                                <FileText className="h-5 w-5 text-blue-600" />
                                                                {t("clientProfile.projectPreferences")}
                                                            </h2>
                                                            {isCurrentUser && canEdit && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        navigate("/client/profilesettings", {
                                                                            state: { activeSection: "preferences" },
                                                                        });
                                                                    }}
                                                                    className="text-blue-600 hover:text-blue-700">
                                                                    <Edit className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                        <div className="space-y-4">
                                                            <div>
                                                                <h3 className="font-medium text-gray-900 mb-2 text-sm">
                                                                    {t("clientProfile.projectTypes")}
                                                                </h3>
                                                                <div className="flex flex-wrap gap-2">
                                                                    {profileData.projectPreferences.projectTypes.map(type => (
                                                                        <Badge
                                                                            key={type}
                                                                            variant="secondary"
                                                                            className="bg-blue-50 text-blue-700 text-xs">
                                                                            {type}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <h3 className="font-medium text-gray-900 mb-2 text-sm">
                                                                    {t("clientProfile.industries")}
                                                                </h3>
                                                                <div className="flex flex-wrap gap-2">
                                                                    {profileData.projectPreferences.industries.map(industry => (
                                                                        <Badge
                                                                            key={industry}
                                                                            variant="secondary"
                                                                            className="bg-purple-50 text-purple-700 text-xs">
                                                                            {industry}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Card>
                                                    {/* Show achievements section */}
                                                    <Card color={undefined} className="p-4 w-full">
                                                        <h2 className="text-lg font-semibold mb-4">{t("clientProfile.achievements")}</h2>
                                                        {clientProgressLoading ? (
                                                            <div className="flex items-center justify-center py-8">
                                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                                                <span className="ml-2 text-gray-600">{t("clientProfile.loadingAchievements")}</span>
                                                            </div>
                                                        ) : clientProgressError ? (
                                                            <div className="text-center py-8">
                                                                <p className="text-red-600 mb-2">{t("clientProfile.failedToLoadAchievements")}</p>
                                                                <button 
                                                                    onClick={() => dispatch(getClientProgress({ userId: displayUserId }))}
                                                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                                                                >
                                                                    {t("clientProfile.retry")}
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                {calculateClientAchievements()
                                                                    .filter(achievement => 
                                                                        isCurrentUser 
                                                                            ? true // Show all achievements for current user
                                                                            : achievement.achieved // Show only achieved for other users
                                                                    )
                                                                    .map((achievement, index) => (
                                                                        <div key={index} className={`p-3 rounded-lg ${
                                                                            achievement.achieved 
                                                                                ? "bg-green-50 border border-green-200" 
                                                                                : "bg-gray-50 border border-gray-200"
                                                                        }`}>
                                                                            <div className="flex items-center gap-2 mb-2">
                                                                                {achievement.icon === "Star" && <Star className={`h-4 w-4 ${achievement.achieved ? "text-yellow-500" : "text-gray-400"}`} />}
                                                                                {achievement.icon === "Award" && <Award className={`h-4 w-4 ${achievement.achieved ? "text-yellow-500" : "text-gray-400"}`} />}
                                                                                {achievement.icon === "Users" && <Users className={`h-4 w-4 ${achievement.achieved ? "text-blue-500" : "text-gray-400"}`} />}
                                                                                {achievement.icon === "Briefcase" && <Briefcase className={`h-4 w-4 ${achievement.achieved ? "text-purple-500" : "text-gray-400"}`} />}
                                                                                <h3 className={`font-medium text-sm ${achievement.achieved ? "text-gray-900" : "text-gray-600"}`}>
                                                                                    {achievement.title}
                                                                                </h3>
                                                                            </div>
                                                                            <p className="text-xs text-gray-600">
                                                                                {achievement.description}
                                                                            </p>
                                                                        </div>
                                                                    ))}
                                                            </div>
                                                        )}
                                                    </Card>
                                                </>
                                            )}
                                            {selectedTab === "projects" && (
                                                <Card color={undefined} className="p-4 w-full">
                                                    {clientStats?.contracts && clientStats.contracts.length > 0 ? (
                                                        clientStats.contracts.map((contract: any) => (
                                                            <div key={contract.id} className="border rounded-lg p-4 mb-4">
                                                                <div className="flex justify-between items-start gap-2 mb-4">
                                                                    <div>
                                                                        <h3 className="text-lg font-medium text-gray-900">
                                                                            {contract.job?.title || contract.title || t("clientProfile.untitledProject")}
                                                                        </h3>
                                                                        <p className="text-xs text-gray-500">
                                                                            {t("clientProfile.started")} {contract.createdAt ? formatDate(contract.createdAt) : t("clientProfile.unknown")}
                                                                        </p>
                                                                    </div>
                                                                    <Badge
                                                                        variant={undefined}
                                                                        className={`text-xs ${getStatusColor(Number(contract.status?.id))}`}>
                                                                        {contract.status?.name || t("clientProfile.unknown")}
                                                                    </Badge>
                                                                </div>
                                                                
                                                                <div className="grid grid-cols-2 gap-4 mb-4 text-xs">
                                                                    <div>
                                                                        <p className="text-gray-500">{t("clientProfile.budget")}</p>
                                                                        <p className="font-medium">
                                                                            {currencySymbol}{contract.amount ? contract.amount.toFixed(2) : "0.00"}
                                                                        </p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-gray-500">{t("clientProfile.deliveryTime")}</p>
                                                                        <p className="font-medium">
                                                                            {calculateDeliveryTime(contract.milestones)}
                                                                        </p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-gray-500">{t("clientProfile.createdDate")}</p>
                                                                        <p className="font-medium">
                                                                            {contract.createdAt ? formatDate(contract.createdAt) : t("clientProfile.notSet")}
                                                                        </p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-gray-500">{t("clientProfile.updatedDate")}</p>
                                                                        <p className="font-medium">
                                                                            {contract.updatedAt ? formatDate(contract.updatedAt) : t("clientProfile.notUpdated")}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                
                                                                {contract.job?.description && (
                                                                    <div className="mb-4">
                                                                        <h4 className="font-medium mb-2 text-sm">{t("clientProfile.description")}</h4>
                                                                        <p className="text-sm text-gray-600">{contract.job.description}</p>
                                                                    </div>
                                                                )}
                                                                
                                                                {contract.professional && (
                                                                    <div className="mb-4">
                                                                        <h4 className="font-medium mb-2 text-sm">{t("clientProfile.professional")}</h4>
                                                                        <div className="flex items-center gap-2">
                                                                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                                                                                {isImageUrl(contract.professional.photo) ? (
                                                                                    <img
                                                                                        src={contract.professional.photo}
                                                                                        alt={contract.professional.name}
                                                                                        className="w-full h-full object-cover"
                                                                                    />
                                                                                ) : (
                                                                                    <User className="w-4 h-4 text-gray-500" />
                                                                                )}
                                                                            </div>
                                                                            <div>
                                                                                <p className="text-sm font-medium">
                                                                                    {contract.professional.firstName + " " + contract.professional.lastName || t("clientProfile.unknownProfessional")}
                                                                                </p>
                                                                                <p className="text-xs text-gray-500">
                                                                                    {contract.professional.title || t("clientProfile.professional")}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                
                                                                {contract.job?.categoryId && (
                                                                    <div>
                                                                        <h4 className="font-medium mb-2 text-sm">{t("clientProfile.category")}</h4>
                                                                        <Badge
                                                                            variant="secondary"
                                                                            className="text-xs">
                                                                            {contract.job.categoryId.name || contract.job.categoryId.title || t("clientProfile.category")}
                                                                        </Badge>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))
                                                                    ) : (
                                                        <div className="text-center py-8">
                                                            <div className="flex justify-center mb-4">
                                                                <Briefcase className="h-12 w-12 text-gray-400" />
                                                            </div>
                                                            <h3 className="text-lg font-medium text-gray-900 mb-2">{t("clientProfile.noProjectsYet")}</h3>
                                                            <p className="text-gray-500 mb-4">
                                                                {t("clientProfile.noProjectsMessage")}
                                                            </p>
                                                            {isCurrentUser && canEdit && (
                                                                <Button
                                                                    onClick={() => navigate("/clients/postajob")}
                                                                    className="bg-blue-600 hover:bg-blue-700 text-white">
                                                                    {t("clientProfile.postFirstJob")}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    )}
                                                </Card>
                                            )}
                                            {selectedTab === "reviews" && (
                                                <ClientReviewsTab 
                                                    userId={adminUserId || displayUserId || ""} 
                                                    isAdminEdit={isAdminEdit}
                                                />
                                            )}
                                            {selectedTab === "hiring" && (
                                                <>
                                                    <Card color={undefined} className="p-4 w-full">
                                                        <h2 className="text-lg font-semibold mb-4">{t("clientProfile.hiringOverview")}</h2>
                                                        {isProfileLoading ? (
                                                            <div className="flex items-center justify-center py-8">
                                                                <Spinner />
                                                                <span className="ml-2 text-gray-600">{t("clientProfile.loadingHiringData")}</span>
                                                            </div>
                                                        ) : clientStats ? (
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                                                <div className="p-3 bg-blue-50 rounded-lg">
                                                                    <h3 className="font-medium text-sm mb-1">{t("clientProfile.activeJobs")}</h3>
                                                                    <p className="text-2xl font-bold">
                                                                        {clientStats?.jobsCount || 0}
                                                                    </p>
                                                                    <p className="text-xs text-gray-600">
                                                                        {t("clientProfile.totalJobsPosted")}
                                                                    </p>
                                                                </div>
                                                                <div className="p-3 bg-green-50 rounded-lg">
                                                                    <h3 className="font-medium text-sm mb-1">{t("clientProfile.totalHires")}</h3>
                                                                    <p className="text-2xl font-bold">
                                                                        {clientStats?.completedContractsCount || 0}
                                                                    </p>
                                                                    <p className="text-xs text-gray-600">{t("clientProfile.successfullyCompleted")}</p>
                                                                </div>
                                                                <div className="p-3 bg-purple-50 rounded-lg">
                                                                    <h3 className="font-medium text-sm mb-1">
                                                                        {t("clientProfile.activeContracts")}
                                                                    </h3>
                                                                    <p className="text-2xl font-bold">
                                                                        {clientStats?.activeContractsCount || 0}
                                                                    </p>
                                                                    <p className="text-xs text-gray-600">{t("clientProfile.currentlyInProgress")}</p>
                                                                </div>
                                                                <div className="p-3 bg-yellow-50 rounded-lg">
                                                                    <h3 className="font-medium text-sm mb-1">{t("clientProfile.successRate")}</h3>
                                                                    <p className="text-2xl font-bold">
                                                                        {clientStats?.jobsCount > 0 
                                                                            ? Math.round((clientStats.completedContractsCount / clientStats.jobsCount) * 100)
                                                                            : 0}%
                                                                    </p>
                                                                    <p className="text-xs text-gray-600">{t("clientProfile.projectCompletionRate")}</p>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="text-center py-8">
                                                                <p className="text-gray-500">{t("clientProfile.noHiringData")}</p>
                                                            </div>
                                                        )}
                                                    </Card>
                                                    <Card color={undefined} className="p-4 w-full">
                                                        <h2 className="text-lg font-semibold mb-4">{t("clientProfile.preferredSkills")}</h2>
                                                        {isProfileLoading ? (
                                                            <div className="flex items-center justify-center py-4">
                                                                <Spinner />
                                                                <span className="ml-2 text-gray-600">{t("clientProfile.loadingSkills")}</span>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div className="flex flex-wrap gap-2 mb-6">
                                                                    {userPreferences?.expertise && userPreferences.expertise.length > 0 ? (
                                                                        userPreferences.expertise.map(skill => (
                                                                            <Badge
                                                                                key={skill}
                                                                                variant="secondary"
                                                                                className="bg-green-50 text-green-700 text-xs">
                                                                                {skill}
                                                                            </Badge>
                                                                        ))
                                                                    ) : (
                                                                        <p className="text-sm text-gray-500 italic">{t("clientProfile.noPreferredSkills")}</p>
                                                                    )}
                                                                </div>
                                                                <h3 className="font-medium text-gray-900 mb-3 text-sm">
                                                                    {t("clientProfile.hiringStatistics")}
                                                                </h3>
                                                                <div className="space-y-3">
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-sm text-gray-600">
                                                                            {t("clientProfile.totalInvested")}
                                                                        </span>
                                                                        <span className="font-medium text-sm">
                                                                            {clientStats?.balance ? `${currencySymbol}${clientStats.balance.toLocaleString()}` : "0"}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-sm text-gray-600">{t("clientProfile.averageProjectValue")}</span>
                                                                        <span className="font-medium text-sm">
                                                                            {clientStats?.jobsCount > 0 && clientStats?.balance 
                                                                                ? `${currencySymbol}${Math.round(clientStats.balance / clientStats.jobsCount).toLocaleString()}`
                                                                                : "0"}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-sm text-gray-600">
                                                                            {t("clientProfile.completionRate")}
                                                                        </span>
                                                                        <span className="font-medium text-sm">
                                                                            {clientStats?.jobsCount > 0 
                                                                                ? Math.round((clientStats.completedContractsCount / clientStats.jobsCount) * 100)
                                                                                : 0}%
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )}
                                                    </Card>
                                                    <Card color={undefined} className="p-4 w-full">
                                                        <h2 className="text-lg font-semibold mb-4">{t("clientProfile.projectPreferences")}</h2>
                                                        {isProfileLoading ? (
                                                            <div className="flex items-center justify-center py-4">
                                                                <Spinner />
                                                                <span className="ml-2 text-gray-600">{t("clientProfile.loadingSkills")}</span>
                                                            </div>
                                                        ) : (
                                                            <div className="space-y-4">
                                                                <div>
                                                                    <h3 className="font-medium text-gray-900 mb-2 text-sm">
                                                                        {t("clientProfile.projectTypes")}
                                                                    </h3>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {userPreferences?.projectTypes && userPreferences.projectTypes.length > 0 ? (
                                                                            userPreferences.projectTypes.map(type => (
                                                                                <Badge
                                                                                    key={type}
                                                                                    variant="secondary"
                                                                                    className="bg-blue-50 text-blue-700 text-xs">
                                                                                    {type}
                                                                                </Badge>
                                                                            ))
                                                                        ) : (
                                                                            <p className="text-sm text-gray-500 italic">{t("clientProfile.noProjectTypes")}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <h3 className="font-medium text-gray-900 mb-2 text-sm">
                                                                        {t("clientProfile.industries")}
                                                                    </h3>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {company?.industry ? (
                                                                            <Badge
                                                                                variant="secondary"
                                                                                className="bg-purple-50 text-purple-700 text-xs">
                                                                                {company.industry.name || company.industry}
                                                                            </Badge>
                                                                        ) : (
                                                                            <p className="text-sm text-gray-500 italic">{t("clientProfile.noIndustrySpecified")}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Card>
                                                </>
                                            )}
                                        </div>
                                        <div className="lg:w-1/3 space-y-6 w-full">
                                            {/* Show Contact Information Section if:
                                                1. User is viewing their own profile (isCurrentUser), OR
                                                2. User is authenticated and has contracts with escrows in status 2 or 3 
                                                   where the contract's professional ID matches the current user's ID */}
                                            {(selectedTab === "overview" || window.innerWidth >= 1024) && (isCurrentUser || hasAcceptedMilestones()) && (
                                                <ContactComponent
                                                    editingContact={editingContact}
                                                    isEditing={isEditing}
                                                    startEditingContact={startEditingContact}
                                                    editedProfile={editedProfile}
                                                    handleCompanyFieldChange={handleCompanyFieldChange}
                                                    validationCompanyErrors={validationCompanyErrors}
                                                    handleSocialLinkChange={handleSocialLinkChange}
                                                    profileData={profileData}
                                                    validationPersonalErrors={validationPersonalErrors}
                                                    handleContactSaveChanges={handleContactSaveChanges}
                                                    setEditingContact={setEditingContact}
                                                    isCurrentUser={isCurrentUser}
                                                    showEditIcon={canEdit && isCurrentUser} // Only show edit icon for current user
                                                    canEdit={canEdit && isCurrentUser} // Only allow editing for current user
                                                    shouldMaskSensitiveInfo={shouldMaskSensitiveInfo}
                                                />
                                            )}
                                            <LanguageComponent
                                                isLanguageEditing={isLanguageEditing}
                                                setIsLanguageEditing={setIsLanguageEditing}
                                                setEditingLanguages={setEditingLanguages}
                                                setHasAddedNewLanguage={setHasAddedNewLanguage}
                                                editingLanguages={editingLanguages}
                                                startEditingLanguage={startEditingLanguage}
                                                preferredLanguages={preferredLanguages}
                                                hasAddedNewLanguage={hasAddedNewLanguage}
                                                isCurrentUser={isCurrentUser}
                                                showEditIcon={canEdit}
                                                canEdit={canEdit}
                                            />
                                            {/* <Card color={undefined} className="p-4 w-full">
                                                <h2 className="text-lg font-semibold mb-4">{t("clientProfile.accountInfo")}</h2>
                                                <div className="space-y-3 text-sm">
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-500">{t("clientProfile.memberSince")}</span>
                                                        <span className="font-medium">{profileData.personal.joined}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-500">{t("clientProfile.lastActive")}</span>
                                                        <span className="font-medium">{profileData.personal.lastActive}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-500">{t("clientProfile.projectsPosted")}</span>
                                                        <span className="font-medium">
                                                            {profileData.personal.projectsPosted}
                                                        </span>
                                                    </div>
                                                </div>
                                            </Card> */}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </main>

                </div>
                <br></br>
                <br></br>
                <br></br>
                <br></br>
                {!isAdminEdit && (
                    <footer className={`bg-white border-t mt-auto w-full ${isCurrentUser && canEdit ? "lg:pl-64" : ""}`}>
                        <div className="max-w-7xl mx-auto">
                            <SharedFooter3 />
                        </div>
                    </footer>
                )}
            </div>
        </>
    );
};

export default ClientProfile;
